import { createListStore } from "./list";
import { TeamsService } from "@/services/teams";
import { Ticket } from "@/types/ticket";
import { Team } from "@/types/team";
import { useFabriqStore } from "@/store/fabriq";

export const useTeamsStore = createListStore(
  "teams",
  TeamsService,
  undefined,
  { sharingSettings: {}, sharingSettingByTeamId: {} },
  {
    async loadTeamsFromTeamSharing(ticket: Ticket) {
      const allTeamsIds = this.collection.map((team: Team) => team.id);
      const missingTeams = (ticket.teams || []).filter(
        (id) => !allTeamsIds.includes(id)
      );
      if (!TeamsService.load) return;
      const load = TeamsService.load;
      await Promise.allSettled(
        missingTeams.map((missingTeam) =>
          load(missingTeam).then((team) => {
            this.merge([
              { ...team, sharingSetting: this.sharingSettingByTeamId[team.id] },
            ]);
          })
        )
      );
    },
    async loadTeamSharingSettings(): Promise<void> {
      const sharingSettings = await TeamsService.loadTeamSharingSettings();
      this.sharingSettings = sharingSettings;
      const teamsById = (this.collection as Team[]).reduce((map, team) => {
        map.set(team.id, team);
        return map;
      }, new Map<number, Team>());

      this.sharingSettingByTeamId = {};
      const editedTeams = new Set<number>();

      for (const sharingSetting of ["team", "organization", "site"] as const) {
        for (const sharedTeam of sharingSettings[sharingSetting] ?? []) {
          this.sharingSettingByTeamId[sharedTeam.id] = sharingSetting;

          const existing = teamsById.get(sharedTeam.id);
          if (!existing) continue;
          teamsById.set(sharedTeam.id, {
            ...existing,
            sharingSetting,
          });
          editedTeams.add(sharedTeam.id);
        }
      }

      teamsById.forEach((team, id) => {
        if (!editedTeams.has(id)) {
          // We remove the sharing settings for the teams where we don't have any anymore.
          teamsById.set(id, {
            ...team,
            sharingSetting: undefined,
          });
        }
      });

      const vals = Array.from(teamsById.values());
      this.merge(vals);
    },
  },
  true,
  {
    teamsForCurrentUser() {
      // eslint-disable-next-line fabriq-webapp/vue-composable-rules -- we're in a store
      const fabriqStore = useFabriqStore();
      const userId = fabriqStore.user?.id;
      if (!userId) return [];
      return this.collection.filter((team: Team) =>
        team.users?.includes(userId)
      );
    },
    teamSharingSettings() {
      return this.sharingSettings;
    },
  }
);
